import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../shared/api/auth';
import { fetchJourneyById } from '../shared/api/journeys';
import { createSession, getSessionById, updateSession } from '../shared/api/sessions';
import { getResumeData, clearResumeData, getSelectedJourney } from '../shared/utils/storage';
import { supabase } from '../shared/supabase';
import { getComponentForTechnique, getTechnique } from '../shared/config/techniqueConfig';
import JourneyChatPage from './JourneyChatPage';
import JournalingSession from './JournalingSession';
import MeditationSession from '../Meditation/MeditationSession';
import AffirmationCardPack from '../Affirmations/AffirmationCardPack';

// ✅ Import centralized theme
import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  colors,
  shadows 
} from '../shared/config/theme';

const Journey = ({ onProgressUpdate, onComplete, onCancel, user, userProfile }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  const [currentStep, setCurrentStep] = useState('chat');
  const [selectedTechnique, setSelectedTechnique] = useState(null);

  useEffect(() => {
    initializeSession();
  }, []);

  const initializeSession = async () => {
    try {
      console.log('🚀 INIT: Starting session initialization...');
      
      const { user, error: userError } = await getCurrentUser();
      if (userError) throw userError;
      
      console.log('✅ INIT: User authenticated:', user.id);
      setUserId(user.id);

      const resumeData = getResumeData();
      let journeyId = getSelectedJourney();
      
      console.log('📦 INIT: Storage data:', {
        resumeSessionId: resumeData?.sessionId,
        resumeStep: resumeData?.currentStep,
        journeyId
      });

      if (resumeData?.sessionId && (!journeyId || journeyId === 'undefined' || journeyId === 'null')) {
        console.log('🔍 INIT: No journey ID in storage, fetching from database...');
        
        const { session, error: sessionError } = await getSessionById(resumeData.sessionId);

        if (sessionError) {
          console.error('❌ INIT: Error fetching session:', sessionError);
          throw new Error('Could not load session from database');
        }

        journeyId = session?.journey_id;
        console.log('✅ INIT: Fetched journey_id from DB:', journeyId);
        
        if (journeyId && journeyId !== 'undefined') {
          sessionStorage.setItem('selectedJourneyId', journeyId);
        }
      }

      if (!journeyId || journeyId === 'undefined' || journeyId === 'null') {
        console.error('❌ INIT: Invalid journey ID:', journeyId);
        throw new Error('No valid journey ID found. Please start a new session.');
      }

      console.log('🔍 INIT: Loading journey with ID:', journeyId);
      
      const { journey, error: journeyError } = await fetchJourneyById(journeyId);

      if (journeyError) {
        console.error('❌ INIT: Error loading journey:', journeyError);
        throw new Error(`Failed to load journey: ${journeyError.message}`);
      }

      if (!journey) {
        console.error('❌ INIT: Journey not found for ID:', journeyId);
        throw new Error('Journey not found in database');
      }

      console.log('✅ INIT: Journey loaded:', journey.title);
      setJourneyData(journey);

      if (resumeData?.sessionId) {
        console.log('🔄 INIT: Resuming session:', resumeData.sessionId);
        console.log('📍 INIT: At step:', resumeData.currentStep);
        
        setSessionId(resumeData.sessionId);
        
        const { session: sessionData } = await getSessionById(resumeData.sessionId);
        
        if (sessionData?.suggested_technique) {
          const technique = getTechnique(sessionData.suggested_technique);
          setSelectedTechnique(technique);
          console.log('✅ INIT: Loaded technique:', technique?.name);
        }
        
        setCurrentStep(resumeData.currentStep || 'chat');
        clearResumeData();
        
      } else {
        console.log('🆕 INIT: Creating new session...');
        
        const { session: newSession, error: sessionError } = await createSession(
          user.id,
          journeyId,
          'chat'
        );

        if (sessionError) {
          console.error('❌ INIT: Failed to create session:', sessionError);
          throw new Error(`Failed to create session: ${sessionError.message}`);
        }

        console.log('✅ INIT: New session created:', newSession.id);
        setSessionId(newSession.id);
      }

      setLoading(false);
      console.log('✅ INIT: Initialization complete!');

    } catch (error) {
      console.error('❌ INIT: Fatal error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleChatComplete = async () => {
    console.log('✅ Chat completed, determining next step...');
    
    try {
      const { session: sessionData, error: fetchError } = await getSessionById(sessionId);

      if (fetchError) {
        console.error('Error fetching technique:', fetchError);
        setCurrentStep('journal');
        return;
      }

      const techniqueId = sessionData?.suggested_technique;
      
      if (techniqueId) {
        const technique = getTechnique(techniqueId);
        setSelectedTechnique(technique);
        
        const component = getComponentForTechnique(techniqueId);
        console.log(`🎯 Routing to: ${component} for technique: ${technique.name}`);
        
        await updateSession(sessionId, { current_step: component });
        
        setCurrentStep(component);
      } else {
        console.log('ℹ️ No technique suggested, using default journal flow');
        setCurrentStep('journal');
      }
    } catch (error) {
      console.error('Error in handleChatComplete:', error);
      setCurrentStep('journal');
    }
  };

  const handleJournalingComplete = async () => {
    console.log('✅ Journaling completed, moving to meditation...');
    
    await updateSession(sessionId, { current_step: 'meditation' });
    
    setCurrentStep('meditation');
  };

  const handleMeditationComplete = async () => {
    console.log('✅ Meditation completed, moving to affirmations...');
    
    await updateSession(sessionId, { current_step: 'affirmations' });
    
    setCurrentStep('affirmations');
  };

  const handleMeditationSkip = async () => {
    console.log('⏭️ Meditation skipped, moving to affirmations...');
    
    await updateSession(sessionId, { current_step: 'affirmations' });
    
    setCurrentStep('affirmations');
  };

  const handleAffirmationsComplete = async () => {
    console.log('✅ Affirmations completed, finishing session...');
    
    await supabase
      .from('daily_sessions')
      .update({ 
        completed_at: new Date().toISOString(),
        current_step: 'complete'
      })
      .eq('id', sessionId);
    
    onComplete();
  };

  const handleChatCancel = () => {
    console.log('🚫 Chat session canceled by user');
    sessionStorage.removeItem('selectedJourneyId');
    clearResumeData();
    
    if (onCancel) {
      onCancel();
    } else if (onComplete) {
      onComplete();
    }
  };

  // ✅ Loading State - Using theme
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7F4F0" }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4" style={{ borderColor: "#EDE7DC", borderTopColor: "#A8856A" }}></div>
          <div className="text-xl" style={{ color: "#9C9080", fontFamily: "Inter, sans-serif" }}>Loading your journey...</div>
        </div>
      </div>
    );
  }

  // ✅ Error State - Using theme
  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7F4F0" }} p-6`}>
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-8 text-center max-w-md shadow-lg">
          <div className="text-6xl mb-4">⚠️</div>
          <div className="text-red-900 text-xl font-bold mb-2">Oops! Something went wrong</div>
          <div className="text-red-700 text-sm mb-4">{error}</div>
          <button 
            onClick={() => {
              sessionStorage.clear();
              window.location.href = '/';
            }}
            className={`${buttonStyles.primary.base} ${buttonStyles.primary.hover} ${buttonStyles.primary.active} ${buttonStyles.primary.transition} ${buttonStyles.primary.rounded} ${buttonStyles.primary.shadow} px-6 py-2`}
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (currentStep === 'chat') {
    return (
      <JourneyChatPage
        userId={userId}
        sessionId={sessionId}
        journeyData={journeyData}
        onProgressUpdate={onProgressUpdate}
        onComplete={handleChatComplete}
        onCancel={handleChatCancel}
        supabase={supabase}
        openaiApiKey={process.env.REACT_APP_OPENAI_API_KEY}
        user={user}
        userProfile={userProfile}
      />
    );
  }

  if (currentStep === 'journal' || currentStep === 'journaling') {
    return (
      <JournalingSession
        journeyData={journeyData}
        userId={userId}
        sessionId={sessionId}
        technique={selectedTechnique}
        onComplete={handleJournalingComplete}
      />
    );
  }

  if (currentStep === 'meditation') {
    return (
      <MeditationSession
        journeyData={journeyData}
        userId={userId}
        sessionId={sessionId}
        technique={selectedTechnique}
        onComplete={handleMeditationComplete}
        onSkip={handleMeditationSkip}
        supabase={supabase}
      />
    );
  }

  if (currentStep === 'affirmations') {
    return (
      <AffirmationCardPack
        journeyId={journeyData.id}
        userId={userId}
        sessionId={sessionId}
        journeyTitle={journeyData.title}
        technique={selectedTechnique}
        onComplete={handleAffirmationsComplete}
      />
    );
  }

  return null;
};

export default Journey;